import { uniq } from 'es-toolkit';
import React, { FunctionComponent } from 'react';
import { Typography } from '@mui/material';

import {
  ComponentMovementRequest,
  getDisplayNameForItem,
  getLayoutModels,
  getSchemaForProjectHierarchyModel,
  moveComponentDesigns,
} from '@paragon/layout-models';
import { InlineNotification } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';

import { useAppDispatch, useAppSelector } from '../hooks';

interface Props {
  readonly requestedMovement: ComponentMovementRequest;
}

export const ComponentMovementConfirmationDialog: FunctionComponent<Props> = ({ requestedMovement }) => {
  const dispatch = useAppDispatch();
  const layoutModels = useAppSelector(getLayoutModels);

  const targetContainer = layoutModels.find((model) => model.guid === requestedMovement.targetContainerGuid)!;
  const schema = getSchemaForProjectHierarchyModel(targetContainer);
  const containerName = getDisplayNameForItem(targetContainer, schema);

  const componentsCount = requestedMovement.insertions.length;
  const componentsLabel = componentsCount === 1 ? 'component design' : 'component designs';

  const componentNamesInRelationships = requestedMovement.componentDesignGuidsInRelationships.map(
    (guid) => layoutModels.find((model) => model.guid === guid)?.name ?? 'unknown',
  );

  return (
    <Dialog
      title="Move Component Designs?"
      onCancel="close"
      submitText="Move"
      onSubmit={() => {
        dispatch(moveComponentDesigns(requestedMovement));
        dispatch(closeCurrentPopup());
      }}
    >
      <Typography>
        Are you sure you want to move {componentsCount} {componentsLabel} into {containerName}?
      </Typography>

      {requestedMovement.someLayoutComponentsIgnored && (
        <InlineNotification type="warning" fullWidthWrap>
          Note that some of the components you selected are assigned to truss envelopes. These will be ignored because
          layout component designs can only be moved in the design client.
        </InlineNotification>
      )}

      {componentNamesInRelationships.length > 0 && (
        <InlineNotification type="warning" fullWidthWrap>
          Note that some of the components you selected are part of a carried component relationship:{' '}
          {uniq(componentNamesInRelationships).join(', ')}. If you continue, these connections will be removed.
        </InlineNotification>
      )}
    </Dialog>
  );
};
